import {
	AuxiliaryComponent,
	Product,
	ProductionCostAnalysisReport,
	ProductionEfficiencyReport,
	ProductionQueueItem,
	ProductionStage,
	ProductionState,
	ProductionStatusOverviewReport,
	ProductionTimeAnalysisReport,
	Supplier,
	TimelineItem,
	UnifiedComponent,
	User,
} from './types';

export const mutations = {
	setQueue(state: ProductionState, payload: { data: ProductionQueueItem[]; meta?: any }) {
		state.queue.data = payload.data;
		state.queue.meta = payload.meta || null;
	},

	setCurrentQueueItem(state: ProductionState, item: ProductionQueueItem | null) {
		state.currentQueueItem = item;
	},

	addToQueue(state: ProductionState, item: ProductionQueueItem) {
		state.queue.data.unshift(item);
	},

	updateQueueItem(state: ProductionState, updatedItem: ProductionQueueItem) {
		const index = state.queue.data.findIndex(item => item.id === updatedItem.id);
		if (index !== -1) {
			state.queue.data.splice(index, 1, updatedItem);
		}
	},

	removeFromQueue(state: ProductionState, id: number) {
		state.queue.data = state.queue.data.filter(item => item.id !== id);
	},

	setProducts(state: ProductionState, products: Product[]) {
		state.products = products;
	},

	setMaterials(state: ProductionState, materials: any[]) {
		state.materials = materials;
	},

	setPlannedMaterials(state: ProductionState, materials: any[]) {
		state.plannedMaterials = materials;
	},

	setPlannedMaterialsByProduct(state: ProductionState, products: any[]) {
		state.plannedMaterialsByProduct = products;
	},

	setMaterialUsage(state: ProductionState, usage: any[]) {
		state.materialUsage = usage;
	},

	setMaterialComparisonByProduct(state: ProductionState, comparison: any) {
		state.materialComparisonByProduct = comparison;
	},

	addProduct(state: ProductionState, product: Product) {
		state.products.push(product);
	},

	updateProduct(state: ProductionState, updatedProduct: Product) {
		const index = state.products.findIndex(p => p.id === updatedProduct.id);
		if (index !== -1) {
			state.products.splice(index, 1, updatedProduct);
		}
	},

	removeProduct(state: ProductionState, id: number) {
		state.products = state.products.filter(p => p.id !== id);
	},

	setSuppliers(state: ProductionState, suppliers: Supplier[]) {
		state.suppliers = suppliers;
	},

	setProductionStages(state: ProductionState, stages: ProductionStage[]) {
		state.productionStages = stages;
	},

	setUsers(state: ProductionState, users: User[]) {
		state.users = users;
	},

	setTimeline(state: ProductionState, timeline: TimelineItem[]) {
		state.timeline = timeline;
	},

	// Auxiliary Components mutations
	setAuxiliaryComponents(state: ProductionState, components: AuxiliaryComponent[]) {
		state.auxiliaryComponents = components;
	},

	addAuxiliaryComponent(state: ProductionState, component: AuxiliaryComponent) {
		state.auxiliaryComponents.push(component);
	},

	updateAuxiliaryComponent(state: ProductionState, updatedComponent: AuxiliaryComponent) {
		const index = state.auxiliaryComponents.findIndex(c => c.id === updatedComponent.id);
		if (index !== -1) {
			state.auxiliaryComponents.splice(index, 1, updatedComponent);
		}
	},

	removeAuxiliaryComponent(state: ProductionState, id: number) {
		state.auxiliaryComponents = state.auxiliaryComponents.filter(c => c.id !== id);
	},

	// Unified Components mutations
	setAllComponents(state: ProductionState, components: UnifiedComponent[]) {
		state.allComponents = components;
	},

	setComponentFilters(
		state: ProductionState,
		filters: Partial<ProductionState['componentFilters']>,
	) {
		state.componentFilters = { ...state.componentFilters, ...filters };
	},

	clearComponentFilters(state: ProductionState) {
		state.componentFilters = {
			type: 'all',
			category: null,
			isInternal: null,
			supplierId: null,
			search: '',
		};
	},

	setFilters(state: ProductionState, filters: Partial<ProductionState['activeFilters']>) {
		state.activeFilters = { ...state.activeFilters, ...filters };
	},

	clearFilters(state: ProductionState) {
		state.activeFilters = {
			status: [],
			priority: null,
			managerId: null,
			placementDate: null,
		};
	},

	setLoading(state: ProductionState, loading: boolean) {
		state.loading = loading;
	},

	setError(state: ProductionState, error: string | null) {
		state.error = error;
	},

	// Reports mutations
	setEfficiencyReport(state: ProductionState, report: ProductionEfficiencyReport | null) {
		state.reports.efficiency = report;
	},

	setTimeAnalysisReport(state: ProductionState, report: ProductionTimeAnalysisReport | null) {
		state.reports.timeAnalysis = report;
	},

	setCostAnalysisReport(state: ProductionState, report: ProductionCostAnalysisReport | null) {
		state.reports.costAnalysis = report;
	},

	setStatusOverviewReport(state: ProductionState, report: ProductionStatusOverviewReport | null) {
		state.reports.statusOverview = report;
	},

	setReportsLoading(state: ProductionState, loading: boolean) {
		state.reports.loading = loading;
	},

	setReportsError(state: ProductionState, error: string | null) {
		state.reports.error = error;
	},
};

export default mutations;
